import React from 'react';
import "../css/ToastProvider.css"
import { ToastSuccessIcon, ToastErrorIcon, ToastWarningIcon } from '../../../../../assets-module/asset-configuration/icon/iconExporter/iconExporter';

export type ToastStatus = 'success' | 'warning' | 'error';

export type ToastItem = {
  id: string;
  status: ToastStatus;
  title: string;
  description?: string;
  duration?: number;
  isExiting?: boolean;
};

type ToastContextValue = {
  showToast: (toast: Omit<ToastItem, 'id'>) => string;
  dismissToast: (id: string) => void;
};

const ToastContext = React.createContext<ToastContextValue | undefined>(undefined);

type ToastProviderProps = {
  children: React.ReactNode;
  placement?: 'bottom-left' | 'bottom-right' | 'bottom-center' | 'top-left' | 'top-right' | 'top-center';
  defaultDuration?: number;
  maxToasts?: number; // Add maxToasts prop
};

// Custom Toast Component
interface ToastProps {
  status: ToastStatus;
  title: string;
  description?: string;
  onClose?: () => void;
}

const Toast: React.FC<ToastProps> = ({ status, title, description, onClose }) => {
  const getIcon = () => {
    switch (status) {
      case 'success':
        return <ToastSuccessIcon width={24} height={24} />;
      case 'error':
        return <ToastErrorIcon width={24} height={24} />;
      case 'warning':
        return <ToastWarningIcon width={24} height={24} />;
        /* istanbul ignore next */
      default:
        return null;
    }
  };

  const showIcon = status === 'success' || status === 'error' || status === 'warning';

  return (
    <div className={`scl--toast scl--toast--${status}`}>
      {/* Radial gradient glow effect */}
      <div className={`scl--toast__glow scl--toast__glow--${status}`} />

      {/* Icon */}
      {showIcon && (
        <div className={`scl--toast__icon scl--toast__icon--${status}`}>
          {getIcon()}
        </div>
      )}

      {/* Text block */}
      <div className={`scl--toast__text-block ${!showIcon //NOSONAR
        ? 'scl--toast__text-block--no-icon' : ''}`}>
        <span className="scl--toast__title">{title}</span>
        {description && (
          <span className="scl--toast__description">{description}</span>
        )}
      </div>

      {/* Bottom glow line */}
      <div className={`scl--toast__bottom-line scl--toast__bottom-line--${status}`} />
      <div className={`scl--toast__bottom-line-blur scl--toast__bottom-line-blur--${status}`} />
    </div>
  );
};

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  placement = 'bottom-left',
  defaultDuration = 5000,
  maxToasts = 5, // Default maximum of 5 toasts
}) => {
  const [toasts, setToasts] = React.useState<ToastItem[]>([]);
  const hideTimers = React.useRef<Record<string, number>>({});
  const removeTimers = React.useRef<Record<string, number>>({});

  const dismissToast = React.useCallback((id: string) => {
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, isExiting: true } : t))
    );

    if (typeof globalThis.window !== 'undefined') {  //NOSONAR 
      removeTimers.current[id] = globalThis.window.setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));  //NOSONAR
        delete removeTimers.current[id];
      }, 300);
    }

    if (typeof globalThis.window !== 'undefined') {  //NOSONAR
      if (hideTimers.current[id]) {
        globalThis.window.clearTimeout(hideTimers.current[id]);
        delete hideTimers.current[id];
      }
    }
  }, []);

  const showToast = React.useCallback((toast: Omit<ToastItem, 'id'>) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`; //NOSONAR

    setToasts((prev) => {
      let updatedToasts = [...prev];

      // If we're at the max limit, mark oldest toasts for exit animation
      const nonExitingToasts = updatedToasts.filter(t => !t.isExiting);
      if (nonExitingToasts.length >= maxToasts) {
        // Get the oldest non-exiting toasts to remove
        const toastsToRemove = nonExitingToasts.slice(maxToasts - 1);
        const idsToRemove = new Set(toastsToRemove.map(t => t.id));

        // Mark them as exiting for animation
        updatedToasts = updatedToasts.map(t =>
          idsToRemove.has(t.id) ? { ...t, isExiting: true } : t
        );

        // Schedule their removal after animation completes
        toastsToRemove.forEach(oldToast => {
          // Clear existing timer
          /* istanbul ignore next */
          if (hideTimers.current[oldToast.id]) {
            globalThis.window?.clearTimeout(hideTimers.current[oldToast.id]);
            delete hideTimers.current[oldToast.id];
          }
          // Remove after exit animation (300ms)
          /* istanbul ignore next */
          if (typeof globalThis.window !== 'undefined') {  //NOSONAR
            removeTimers.current[oldToast.id] = globalThis.window.setTimeout(() => {  //NOSONAR
              setToasts((current) => current.filter((t) => t.id !== oldToast.id));
              delete removeTimers.current[oldToast.id];
            }, 300);
          }
        });
      }

      // Add new toast at the beginning
      return [{ id, ...toast }, ...updatedToasts];
    });

    const duration = toast.duration ?? defaultDuration; 
    if (typeof globalThis.window !== 'undefined') {  //NOSONAR
      hideTimers.current[id] = globalThis.window.setTimeout(() => {
        dismissToast(id);
      }, duration);
    }
    return id;
  }, [defaultDuration, maxToasts, dismissToast]);

  React.useEffect(() => () => {
    if (typeof globalThis.window === 'undefined') return;  //NOSONAR
    Object.values(hideTimers.current).forEach((t) => globalThis.window.clearTimeout(t));
    Object.values(removeTimers.current).forEach((t) => globalThis.window.clearTimeout(t));
  }, []);

  const placementClass = React.useMemo(() => {
    switch (placement) {
      case 'bottom-right':
        return 'scl--toastProvider__stack--br';
      case 'bottom-center':
        return 'scl--toastProvider__stack--bc';
      case 'top-left':
        return 'scl--toastProvider__stack--tl';
      case 'top-right':
        return 'scl--toastProvider__stack--tr';
      case 'top-center':
        return 'scl--toastProvider__stack--tc';
      default:
        return 'scl--toastProvider__stack--bl';
    }
  }, [placement]);

  return (
    <ToastContext.Provider value={{ showToast, dismissToast }}  //NOSONAR
    > 
      {children}
      <div className={`scl--toastProvider__stack ${placementClass}`}>
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`scl--toastProvider__item ${t.isExiting ? 'tp-exit' : ''}`}
          >
            <Toast
              status={t.status}
              title={t.title}
              description={t.description}
              onClose={() => dismissToast(t.id)}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};